import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchInput, sanitizeString } from '../utils/security';
import { fetchBayiler, updateBayiKullanici } from '../services/bayiYonetimService';
import { fetchCities } from '../services/locationsService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import AktifSayiKart from '../components/AktifSayiKart';
import { ROWS_PER_PAGE_OPTIONS } from '../constants/pagination';

const rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS;
const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  const message = error?.message || error?.data?.mesaj || fallback;
  if (message === 'Failed to fetch') return 'Sunucuya bağlanılamadı.';
  return message || fallback;
};

const BayiManagementPage = () => {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const [bayiler, setBayiler] = useState([]);
  const [filters, setFilters] = useState({ search: '', cityId: 'all' });
  const [activeStatusTab, setActiveStatusTab] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [aktifSayisi, setAktifSayisi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [iller, setIller] = useState([]);
  const loadRequestSeqRef = useRef(0);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const loadBayiler = useCallback(async () => {
    const reqId = ++loadRequestSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await fetchBayiler({
        page: currentPage,
        limit: rowsPerPage,
        aktif: activeStatusTab === 'active',
        search: sanitizedSearchValue,
        cityId: filters.cityId,
      });
      if (reqId !== loadRequestSeqRef.current) return;
      const veri = response?.veri || {};
      const list = veri.bayiler || [];
      const totalVal = veri.toplam ?? list.length;
      const totalPg = veri.toplamSayfa || Math.ceil(totalVal / rowsPerPage) || 1;
      setBayiler(list);
      setPaginationMeta({ total: totalVal, totalPages: totalPg });
      if (veri.aktifSayisi != null) setAktifSayisi(veri.aktifSayisi);
      if (currentPage > totalPg && totalPg > 0) setCurrentPage(totalPg);
    } catch (err) {
      if (reqId !== loadRequestSeqRef.current) return;
      setError(getErrorMessage(err, 'Bayiler yüklenemedi'));
      setBayiler([]);
    } finally {
      if (reqId === loadRequestSeqRef.current) setLoading(false);
    }
  }, [currentPage, rowsPerPage, activeStatusTab, sanitizedSearchValue, filters.cityId]);

  useEffect(() => {
    loadBayiler();
  }, [loadBayiler]);

  useEffect(() => {
    fetchCities()
      .then((response) => {
        const list = response?.veri?.iller || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      })
      .catch(() => setIller([]));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const next = searchInput.trim();
      setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
      setCurrentPage(1);
      setSearchLoading(false);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleToggleAktif = async (bayi) => {
    const aktif = bayi.aktif !== false;
    const ad = bayi.bayiAdi || 'Bayi';
    const confirmed = await confirm({
      title: aktif ? 'Bayiyi pasif yap' : 'Bayiyi aktif yap',
      message: aktif
        ? `${ad} pasif yapılacak. Onaylıyor musunuz?`
        : `${ad} aktif yapılacak. Onaylıyor musunuz?`,
      variant: aktif ? 'warning' : 'info',
      confirmLabel: 'Evet',
    });
    if (!confirmed) return;
    try {
      const response = await updateBayiKullanici(bayi.id, { aktif: !aktif });
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || 'Durum güncellenemedi');
      }
      if (window.showNotification) {
        window.showNotification('success', aktif ? 'Bayi pasif yapıldı' : 'Bayi aktif yapıldı');
      }
      loadBayiler();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Durum güncellenemedi'));
      }
    }
  };

  const yerMetni = (bayi) => {
    const parts = [bayi.ilAdi, bayi.ilceAdi].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  const yetkiliMetni = (bayi) => {
    const ad = [bayi.yetkili?.ad, bayi.yetkili?.soyad].filter(Boolean).join(' ');
    return ad || bayi.yetkili?.kullaniciAdi || '—';
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Bayiler yükleniyor</div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    if (bayiler.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Kayıt bulunamadı</div>
        </div>
      );
    }
    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Bayi Adı</th>
            <th>Yetkili</th>
            <th>İl / İlçe</th>
            <th>Telefon</th>
            <th>E-posta</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {bayiler.map((bayi) => (
            <tr key={bayi.id}>
              <td>
                <span className="tech-service-name-link" onClick={() => navigate(`/bayiler/${bayi.id}`)}>
                  {bayi.bayiAdi || '—'}
                </span>
              </td>
              <td>{yetkiliMetni(bayi)}</td>
              <td>{yerMetni(bayi)}</td>
              <td>{bayi.telefon || '—'}</td>
              <td>{bayi.mail || '—'}</td>
              <td className="table-actions islemler-sutunu">
                {bayi.aktif !== false ? (
                  <button
                    type="button"
                    className="category-action-btn delete-btn"
                    aria-label="Bayiyi pasif yap"
                    title="Pasif yap"
                    onClick={() => handleToggleAktif(bayi)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="category-action-btn move-btn"
                    aria-label="Bayiyi aktif yap"
                    title="Aktif yap"
                    onClick={() => handleToggleAktif(bayi)}
                  >
                    <i className="fas fa-check"></i>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <main className="main-content tech-service-management">
      <div className="content-header">
        <h1>Bayi Yönetimi</h1>
      </div>

      <AktifSayiKart label="Aktif bayi" value={aktifSayisi} icon="fas fa-store" />

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'active' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('active');
            setCurrentPage(1);
          }}
        >
          Aktif Bayiler
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'passive' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('passive');
            setCurrentPage(1);
          }}
        >
          Pasif Bayiler
        </button>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Ad, telefon veya e-posta ile ara (min 3 karakter)..."
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setSearchLoading(true);
              }}
              maxLength={100}
              autoComplete="off"
            />
            {searchLoading && <i className="fas fa-spinner fa-spin" style={{ right: '10px' }} aria-hidden />}
          </div>
          <div className={`role-filter-dropdown ${filters.cityId !== 'all' ? 'filter-active' : ''}`}>
            <i className="fas fa-map-marker-alt role-filter-icon"></i>
            <select
              value={filters.cityId}
              aria-label="İl filtreleme"
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, cityId: event.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="all">Tüm iller</option>
              {iller.map((il) => (
                <option key={il.id || il._id} value={String(il.id || il._id)}>
                  {il.name || il.ad}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="table-container">{renderTable()}</div>

      <TableListFooter
        totalRecords={paginationMeta.total || bayiler.length}
        currentPage={currentPage}
        totalPages={paginationMeta.totalPages || 1}
        onPageChange={setCurrentPage}
        loading={loading}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={(value) => {
          if (rowsPerPageOptions.includes(value)) {
            setRowsPerPage(value);
            setCurrentPage(1);
          }
        }}
      />
    </main>
  );
};

export default BayiManagementPage;
